/**
 * menuImportRunner.js
 * Executes a validated menu-import plan against the existing restaurant
 * endpoints. Split out of the modal so the sequencing can be tested without
 * mounting React: every dependency is injected.
 *
 * It reuses the audited, RBAC-protected endpoints the Menu Management drawer
 * already calls. Nothing new is trusted to the client: price_ugx goes up as
 * the gross price and the server derives commissionRate, commissionAmount,
 * vendorNetPrice and country itself.
 *
 * Ordering matters. Sections must exist before products can reference a
 * sectionId, and option groups before products can carry their ids, so the
 * three phases run in sequence. Within the product phase each row is
 * independent: one bad row is recorded and skipped rather than aborting the
 * run, because a half-imported menu is easier to finish than to unpick.
 */

const first = (...vals) => vals.find((v) => v !== undefined && v !== null);

/** Endpoints return either {data: doc} or the doc itself depending on age. */
const unwrap = (res) => {
  const body = res && res.data !== undefined ? res.data : res;
  if (body && body.data !== undefined) return body.data;
  return body;
};

const errText = (err, fallback) =>
  first(
    err && err.response && err.response.data && err.response.data.message,
    err && err.message,
    fallback
  );

/**
 * @param {object} plan   output of validateImport
 * @param {object} deps
 * @param {object} deps.restaurant           { _id, owner }
 * @param {Function} deps.createSection      (restaurantId, body) => res
 * @param {Function} deps.fetchSections      (restaurantId) => res
 * @param {Function} deps.createOption       (body) => res
 * @param {Function} deps.createOptionGroup  (body) => res
 * @param {Function} deps.createProduct      (body, restaurantId) => res
 * @param {Function} deps.updateProduct      (productId, body) => res
 * @param {boolean}  [deps.updateExisting]   true (default): rows matched to a
 *   dish already on the menu (plan item action "update") are updated in place;
 *   false: those rows are left untouched. Either way they are never duplicated.
 * @param {boolean}  [deps.forceDisabled]    true (default): every NEW dish is
 *   created enabled:false whatever its `enabled` column says, so the menu can
 *   be checked and activated from the drawer before customers see it. Dishes
 *   that already exist keep the sheet's value (an update never re-hides a
 *   dish that is already live unless the sheet says so).
 * @param {Function} deps.uploadImage        (file, opts) => { secure_url }
 * @param {Function} [deps.onProgress]       ({ phase, done, total, label })
 * @returns {Promise<{created, updated, skipped, failed, log}>}
 */
export const runMenuImport = async (plan, deps) => {
  const {
    restaurant,
    createSection,
    fetchSections,
    createOption,
    createOptionGroup,
    createProduct,
    updateProduct,
    updateExisting = true,
    forceDisabled = true,
    uploadImage,
    onProgress = () => {},
  } = deps;

  const restaurantId = restaurant._id;
  const optionOwner = first(restaurant.owner, restaurant._id);

  const log = [];
  const note = (level, message) => log.push({ level, message });

  const created = { sections: 0, optionGroups: 0, products: 0, images: 0 };
  const updated = { products: 0 };
  const skipped = { products: 0 };
  const failed = { sections: 0, optionGroups: 0, products: 0 };

  const isUpdate = (i) => i.action === "update" && i.existingId;
  const allOk = plan.items.filter((i) => i.ok);
  // Matched rows the operator chose to leave alone are reported, not worked.
  const okItems = allOk.filter((i) => updateExisting || !isUpdate(i));
  allOk
    .filter((i) => !updateExisting && isUpdate(i))
    .forEach((i) => {
      skipped.products += 1;
      note("info", `${i.name}: already on the menu, left untouched`);
    });

  const groupsNeeded = new Set();
  okItems.forEach((i) => i.optionGroupNames.forEach((n) => groupsNeeded.add(n.toLowerCase())));

  const totalSteps =
    plan.sections.filter((s) => s.action === "create").length +
    plan.optionGroups.filter((g) => groupsNeeded.has(g.name.toLowerCase())).length +
    okItems.length;
  let step = 0;
  const tick = (phase, label) => {
    step += 1;
    onProgress({ phase, done: step, total: totalSteps, label });
  };

  // ── Phase 1: sections ─────────────────────────────────────────────────────
  const sectionIdByTitle = new Map();
  plan.sections
    .filter((s) => s.action === "existing")
    .forEach((s) => sectionIdByTitle.set(s.title.toLowerCase(), s.existingId));

  for (const section of plan.sections.filter((s) => s.action === "create")) {
    tick("sections", section.title);
    try {
      const res = await createSection(restaurantId, {
        title: section.title,
        description: section.description || "",
        restaurantId,
      });
      const doc = unwrap(res);
      if (doc && doc._id) {
        sectionIdByTitle.set(section.title.toLowerCase(), doc._id);
        created.sections += 1;
        note("ok", `Section created: ${section.title}`);
      } else {
        failed.sections += 1;
        note("error", `Section "${section.title}" returned no id`);
      }
    } catch (err) {
      failed.sections += 1;
      note("error", `Section "${section.title}" failed: ${errText(err, "unknown error")}`);
    }
  }

  // Sections whose create call did not hand back an id (older responses) can
  // still be recovered by re-reading the restaurant's sections once.
  const unresolved = plan.sections.filter(
    (s) => !sectionIdByTitle.get(s.title.toLowerCase())
  );
  if (unresolved.length && typeof fetchSections === "function") {
    try {
      const res = await fetchSections(restaurantId);
      const list = unwrap(res);
      (Array.isArray(list) ? list : []).forEach((s) => {
        if (s && s.title && s._id && !sectionIdByTitle.get(s.title.toLowerCase())) {
          sectionIdByTitle.set(s.title.toLowerCase(), s._id);
        }
      });
    } catch (err) {
      note("warn", `Could not re-read sections: ${errText(err, "unknown error")}`);
    }
  }

  // ── Phase 2: option groups ────────────────────────────────────────────────
  const groupIdByName = new Map();
  for (const group of plan.optionGroups) {
    if (!groupsNeeded.has(group.name.toLowerCase())) continue; // nothing uses it
    tick("options", group.name);
    try {
      const optionIds = [];
      for (const opt of group.options) {
        const res = await createOption({
          name: opt.name,
          price: opt.price || 0,
          owner: optionOwner,
        });
        const doc = unwrap(res);
        if (doc && doc._id) optionIds.push(doc._id);
      }
      if (!optionIds.length) throw new Error("no options could be created");

      // createRestaurantOptionGroup reads option.value for each entry.
      const res = await createOptionGroup({
        name: group.name,
        options: optionIds.map((id) => ({ value: id })),
        minOptions: group.minOptions || 0,
        maxOptions: group.maxOptions || 0,
        owner: optionOwner,
      });
      const doc = unwrap(res);
      if (doc && doc._id) {
        groupIdByName.set(group.name.toLowerCase(), doc._id);
        created.optionGroups += 1;
        note("ok", `Option group created: ${group.name} (${optionIds.length} options)`);
      } else {
        failed.optionGroups += 1;
        note("error", `Option group "${group.name}" returned no id`);
      }
    } catch (err) {
      failed.optionGroups += 1;
      note("error", `Option group "${group.name}" failed: ${errText(err, "unknown error")}`);
    }
  }

  // ── Phase 3: products ─────────────────────────────────────────────────────
  const uploadOne = async (source, purpose, itemName) => {
    // No photograph for this dish: import it without one rather than failing
    // it. "" is what the model stores for a dish awaiting its shoot, and every
    // client renders its own "no photo yet" tile from that. Never substitute a
    // placeholder URL here - it would upload one Cloudinary asset per dish and
    // read as a real photograph to everything that checks.
    if (!source || (source.kind !== "url" && source.kind !== "file")) return "";
    if (source.kind === "url") return source.url;
    const res = await uploadImage(source.file, {
      skipValidation: true,
      vendorMedia: {
        vendorType: "restaurant",
        vendorId: restaurantId,
        assetPurpose: purpose,
        entityType: "restaurantProduct",
        entityId: itemName,
        source: "dashboard-menu-import",
      },
    });
    const url = res && res.secure_url;
    if (!url) throw new Error("upload returned no URL");
    created.images += 1;
    return url;
  };

  for (const item of okItems) {
    tick("products", item.name);
    const sectionId = sectionIdByTitle.get(item.sectionTitle.toLowerCase());
    if (!sectionId) {
      failed.products += 1;
      note("error", `${item.name}: section "${item.sectionTitle}" is not available, skipped`);
      continue;
    }

    try {
      const image = await uploadOne(item.cover, "product_cover", item.name);

      const additionalImages = [];
      for (const extra of item.additional) {
        try {
          additionalImages.push(await uploadOne(extra, "product_angle", item.name));
        } catch (err) {
          // An extra angle is never worth failing the product over.
          note("warn", `${item.name}: extra angle "${extra.filename}" failed to upload`);
        }
      }

      const optionGroups = item.optionGroupNames
        .map((n) => groupIdByName.get(n.toLowerCase()))
        .filter(Boolean);

      if (isUpdate(item)) {
        if (typeof updateProduct !== "function") {
          throw new Error("this dish already exists and no update endpoint was provided");
        }
        // UPDATE IN PLACE. The row is the source of truth for what it states;
        // what it leaves blank is left alone: no photo in the row keeps the
        // dish's current photos, an empty description keeps the current one,
        // no option groups keeps the current groups. The update endpoint
        // treats an absent key as "unchanged", so those keys are simply not
        // sent. Sending image "" would be ignored anyway; sending
        // additionalImages [] would delete every saved angle.
        const body = {
          name: item.name,
          sectionId,
          restaurantId,
          price: Math.round(item.price),
          enabled: item.enabled,
          requiredPaymentMethod: item.requiredPaymentMethod || "NONE",
        };
        if (item.description) body.description = item.description;
        if (image) body.image = image;
        if (additionalImages.length) body.additionalImages = additionalImages;
        if (item.countInStock !== null && item.countInStock !== undefined) {
          body.countInStock = Math.round(item.countInStock);
        }
        if (Array.isArray(item.tags) && item.tags.length) body.tags = item.tags;
        if (optionGroups.length) body.optionGroups = optionGroups.map((id) => ({ _id: id }));

        await updateProduct(item.existingId, body);
        updated.products += 1;
        note("ok", `Product updated: ${item.name}`);
        continue;
      }

      await createProduct(
        {
          name: item.name,
          description: item.description || "",
          sectionId,
          restaurantId,
          price: Math.round(item.price),
          image,
          additionalImages,
          enabled: forceDisabled ? false : item.enabled,
          optionGroups,
          countInStock:
            item.countInStock === null || item.countInStock === undefined
              ? 1
              : Math.round(item.countInStock),
          tags: item.tags,
          requiredPaymentMethod: item.requiredPaymentMethod || "NONE",
        },
        restaurantId
      );
      created.products += 1;
      note("ok", `Product created: ${item.name}`);
    } catch (err) {
      failed.products += 1;
      note("error", `${item.name} failed: ${errText(err, "unknown error")}`);
    }
  }

  return { created, updated, skipped, failed, log };
};

export default runMenuImport;
